import { Component, Input, OnInit, ChangeDetectorRef } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { FormularioPostModel, FormularioPerguntaPostModel, TipoPergunta, TipoPerguntaLabel } from 'app/models/formulario.model';
import { FormularioService } from 'app/services/formulario.service';
import { NotificacaoService } from 'app/services/notificacao.service';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';

@Component({
    selector: 'app-formulario-builder',
    templateUrl: './formulario-builder.component.html',
    styleUrls: ['./formulario-builder.component.scss'],
    standalone: false
})
export class FormularioBuilderComponent implements OnInit {
    @Input() formularioId: number = 0;

    formularioForm: FormGroup;
    loading = false;
    tiposPergunta = Object.entries(TipoPerguntaLabel).map(([key, label]) => ({ value: Number(key), label }));

    TipoPergunta = TipoPergunta;

    constructor(
        private activeModal: NgbActiveModal,
        private formBuilder: FormBuilder,
        private formularioService: FormularioService,
        private notificacaoService: NotificacaoService,
        private cdr: ChangeDetectorRef
    ) {
        this.formularioForm = this.formBuilder.group({
            id: [0],
            nome: ['', [Validators.required, Validators.maxLength(200)]],
            descricao: [''],
            ativo: [true],
            perguntas: this.formBuilder.array([])
        });
    }

    async ngOnInit(): Promise<void> {
        if (this.formularioId > 0) {
            this.loading = true;
            try {
                const formulario = await this.formularioService.obterPorId(this.formularioId);
                this.formularioForm.patchValue({
                    id: formulario.id,
                    nome: formulario.nome,
                    descricao: formulario.descricao,
                    ativo: formulario.ativo
                });
                formulario.perguntas.forEach(p => this.adicionarPerguntaExistente(p));
            } catch (err) {
                this.notificacaoService.mensagemErro('Erro ao carregar formulário');
                this.activeModal.close(false);
            } finally {
                this.loading = false;
            }
        }
    }

    get perguntas(): FormArray {
        return this.formularioForm.get('perguntas') as FormArray;
    }

    adicionarPergunta(): void {
        this.perguntas.push(this.criarPerguntaForm());
        this.cdr.detectChanges();
    }

    trackByIndex(index: number): number {
        return index;
    }

    private adicionarPerguntaExistente(pergunta: any): void {
        this.perguntas.push(this.criarPerguntaForm(pergunta));
    }

    private criarPerguntaForm(pergunta?: any): FormGroup {
        return this.formBuilder.group({
            id: [pergunta?.id || 0],
            texto: [pergunta?.texto || '', [Validators.required, Validators.maxLength(500)]],
            tipoPergunta: [pergunta?.tipoPergunta || TipoPergunta.TextoCurto],
            ordem: [pergunta?.ordem || this.perguntas.length],
            obrigatoria: [pergunta?.obrigatoria ?? false],
            opcoes: [pergunta?.opcoes || ''],
            escalaMinimo: [pergunta?.escalaMinimo],
            escalaMaximo: [pergunta?.escalaMaximo]
        });
    }

    removerPergunta(index: number): void {
        this.perguntas.removeAt(index);
        this.atualizarOrdem();
    }

    drop(event: CdkDragDrop<FormGroup[]>): void {
        moveItemInArray(this.perguntas.controls, event.previousIndex, event.currentIndex);
        this.atualizarOrdem();
    }

    private atualizarOrdem(): void {
        this.perguntas.controls.forEach((control, index) => {
            control.get('ordem')?.setValue(index);
        });
    }

    getTipoPergunta(index: number): number {
        return this.perguntas.at(index).get('tipoPergunta')?.value;
    }

    precisaOpcoes(tipo: number): boolean {
        return tipo === TipoPergunta.EscolhaUnica || tipo === TipoPergunta.EscolhaMultipla;
    }

    precisaEscala(tipo: number): boolean {
        return tipo === TipoPergunta.Escala;
    }

    cancelar(): void {
        this.activeModal.close(false);
    }

    async confirmar(): Promise<void> {
        if (this.perguntas.length === 0) {
            this.notificacaoService.mensagemAlerta('Adicione pelo menos uma pergunta ao formulário.');
            return;
        }

        if (this.formularioForm.invalid) {
            this.formularioForm.markAllAsTouched();
            this.notificacaoService.mensagemAlerta('Preencha todos os campos obrigatórios.');
            return;
        }

        this.loading = true;
        try {
            const model = this.montarFormulario();
            if (!this.formularioId) {
                await this.adicionar(model);
            } else {
                await this.atualizar(model);
            }
        } finally {
            this.loading = false;
        }
    }

    private montarFormulario(): FormularioPostModel {
        const form = this.formularioForm.value;
        const model = new FormularioPostModel();
        model.id = form.id;
        model.nome = form.nome;
        model.descricao = form.descricao;
        model.ativo = form.ativo;
        model.perguntas = form.perguntas.map((p: any, index: number) => {
            const pergunta = new FormularioPerguntaPostModel();
            pergunta.id = p.id;
            pergunta.texto = p.texto;
            pergunta.tipoPergunta = p.tipoPergunta;
            pergunta.ordem = index;
            pergunta.obrigatoria = p.obrigatoria;
            pergunta.opcoes = p.opcoes;
            pergunta.escalaMinimo = p.escalaMinimo;
            pergunta.escalaMaximo = p.escalaMaximo;
            return pergunta;
        });
        return model;
    }

    private async adicionar(model: FormularioPostModel): Promise<void> {
        this.formularioService.adicionar(model).subscribe({
            next: (resp) => {
                this.notificacaoService.mensagemSucesso(resp.mensagem);
                this.activeModal.close(true);
            },
            error: (err) => {
                this.notificacaoService.mensagemErro(err?.error?.mensagem || 'Erro ao criar formulário');
            }
        });
    }

    private async atualizar(model: FormularioPostModel): Promise<void> {
        this.formularioService.atualizar(model).subscribe({
            next: (resp) => {
                this.notificacaoService.mensagemSucesso(resp.mensagem);
                this.activeModal.close(true);
            },
            error: (err) => {
                this.notificacaoService.mensagemErro(err?.error?.mensagem || 'Erro ao atualizar formulário');
            }
        });
    }
}
